import { AccountTreeRounded, ArrowBackRounded, CloseRounded, DoneRounded, MoveDownRounded, ReportProblemRounded } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Chip, IconButton, Paper, Radio, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { Dispatch, useState } from 'react'
import ApiClient from '../../../backend/ApiClient'
import algo from '../../../core/Algo'
import { CopyFromFlowItem, IFlow, NodeType } from '../../../definitions/Nodes'
import { ALL_NODES_DESCRIPTION, IAcceptedData } from '../../../definitions/NodesDescription'
import SmartCard from '../../controls/cards/SmartCard'
import { Column, SmartTable } from '../../controls/table'
import useCommand from '../../hooks/useCommand'
import useInvalidate from '../../hooks/useInvalidate'
import { buildQueryKey } from '../../hooks/useQuery'
import { snackbar } from '../../services/SnackbarService'

interface P {
    flow: IFlow
    setOpenBatchUpdateModal: Dispatch<React.SetStateAction<boolean>>
}

const BASE_NODE_REQUIRED_KEYS = [
    'uuid',
    'name',
    'coords',
    'type',
    'inputs',
    'data'
]

const hasAllKeys = (obj: IFlow['nodes'][0] | IFlow['nodes'][0]['data'], keys: string[]) => {
    for (const k of keys) {
        if (!(k in obj)) return false
    }
    return true
}

const isValidCoords = (coords: { x: number, y: number }) => {
    return (
        algo.hasVal(coords) &&
        typeof coords === 'object' &&
        typeof coords.x === 'number' &&
        typeof coords.y === 'number'
    )
}

const isValidNode = (node: IFlow['nodes'][0]) => {
    if (!algo.hasVal(node) || typeof node !== 'object') return false

    if (!hasAllKeys(node, BASE_NODE_REQUIRED_KEYS)) return false

    if (typeof node.uuid !== 'string' || !algo.hasVal(node.uuid)) return false
    if (typeof node.name !== 'string' || !algo.hasVal(node.name)) return false
    if (!isValidCoords(node.coords)) return false

    if (!ALL_NODES_DESCRIPTION.find(n => n.type === node.type)?.type) return false

    if (!Array.isArray(node.inputs)) return false

    for (const input of node.inputs) {
        if (!algo.hasVal(input) || typeof input !== 'object') return false
        if (typeof input.uuid !== 'string') return false
        if (typeof input.outputKey !== 'string') return false
    }

    if (!algo.hasVal(node.data) || typeof node.data !== 'object') return false

    const requiredDataKeys = ALL_NODES_DESCRIPTION.find(n => n.type === node.type)?.acceptedData?.map((a: IAcceptedData<NodeType>) => a.parameter)
    if (algo.hasVal(requiredDataKeys) && !hasAllKeys(node.data, requiredDataKeys)) {
        return false
    }

    return true
}

const isClipboardValid = (obj: { nodes: IFlow['nodes'], globals: IFlow['globals'] }) => {
    if (!algo.hasVal(obj) || typeof obj !== 'object') return false

    const { nodes, globals } = obj

    if (!Array.isArray(nodes)) return false
    if (!algo.hasVal(globals) || typeof globals !== 'object' || Array.isArray(globals)) {
        return false
    }

    for (const node of nodes) {
        if (!isValidNode(node)) return false
    }

    return true
}

const UpdateFlowModal = ({ flow, setOpenBatchUpdateModal }: P) => {
    const [pageUpdateFlowModal, setPageUpdateFlowModal] = useState(1)
    const [listUpdateFlowModal, setListUpdateFlowModal] = useState<CopyFromFlowItem[]>(null)
    const [selectedFlowUUID, setSelectedFlowUUID] = useState<string>(null)
    const invalidateFlow = useInvalidate({ queryKey: buildQueryKey(`get-flow-${flow.uuid}`) })

    const { status: getListCopyFromFlowStatus, execute: getListCopyFromFlowExecute } = useCommand({
        commandFn: async () => await ApiClient.Flow.getListCopyFromFlow(),
        onFinish: result => {
            if (result.status === 'success')
                setListUpdateFlowModal(result.response)

            if (result.status === 'error')
                snackbar.show({ severity: 'error', text: result.errorText })
        }
    })

    const { status: replaceFromFlowStatus, execute: replaceFromFlow } = useCommand({
        commandFn: async () => {
            const fromFlow = await ApiClient.Flow.getFlow(selectedFlowUUID)
            return await ApiClient.Flow.replaceFlow(flow.uuid, { nodes: fromFlow.nodes, globals: fromFlow.globals })
        },
        onFinish: result => {
            if (result.status === 'success') {
                invalidateFlow()
                snackbar.show({ severity: 'success', text: 'Flow replaced with success' })
            }

            if (result.status === 'error')
                snackbar.show({ severity: 'error', text: 'Failed to replate the flow' })
        }
    })

    const { status: replaceFromClipboardStatus, execute: replaceFromClipboard } = useCommand({
        commandFn: async () => {
            const text = await navigator.clipboard.readText()

            let payload: { nodes: IFlow['nodes'], globals: IFlow['globals'] }
            try {
                payload = JSON.parse(text)
            } catch {
                throw new Error('Clipboard content is not a valid JSON.')
            }

            const isValid = isClipboardValid(payload)

            if (!isValid) {
                throw new Error('Clipboard content is not a valid flow')
            }

            return await ApiClient.Flow.replaceFlow(flow.uuid, payload)
        },
        onFinish: result => {
            if (result.status === 'success') {
                invalidateFlow()
                snackbar.show({ severity: 'success', text: 'Flow replaced from clipboard' })
            }

            if (result.status === 'error')
                snackbar.show({ severity: 'error', text: result.errorText })
        }
    })

    const renderReplaceFlowModal = () => {
        return (
            <Stack spacing={3}>
                <Stack flexDirection='row' justifyContent='space-between'>
                    <Typography variant='h6'>Update the settlement</Typography>
                    <IconButton
                        size='small'
                        onClick={() => {
                            setOpenBatchUpdateModal(false)
                            setPageUpdateFlowModal(1)
                        }}>
                        <CloseRounded fontSize='small' />
                    </IconButton>
                </Stack>
                <Stack flexDirection='row' gap={3}>
                    <Stack alignItems='center' spacing={2} flex={1}>
                        <AccountTreeRounded fontSize='large' color='primary' />
                        <Typography variant='subtitle1'>Copy from approved flow</Typography>
                        <Typography variant='body2' textAlign='center'>Select an approved flow to replace this one. Choosing a flow overwrites all nodes and connections in the current flow immediately.</Typography>
                        <Button
                            onClick={() => {
                                getListCopyFromFlowExecute(null)
                                setPageUpdateFlowModal(2)
                            }}
                            variant='contained'>Select flow
                        </Button>
                    </Stack>
                    <Stack alignItems='center' spacing={2} flex={1}>
                        <MoveDownRounded fontSize='large' color='secondary' />
                        <Typography variant='subtitle1'>Replace from clipboard</Typography>
                        <Typography variant='body2' textAlign='center'>Replace this flow with the nodes and connections in your clipboard. This overwrites the entire flow and cannot be undone.</Typography>
                        <LoadingButton
                            loading={replaceFromClipboardStatus === 'loading'}
                            onClick={() => {
                                setPageUpdateFlowModal(3)
                            }}
                            variant='contained'
                            color='secondary'>paste and replace
                        </LoadingButton>
                    </Stack>
                </Stack>
            </Stack>
        )
    }

    const renderSelectFlowModal = () => {
        const columns: Column<CopyFromFlowItem>[] = [
            {
                id: 'action', label: 'Selection', customRenderer: cell => {
                    return (
                        <Box key={selectedFlowUUID}>
                            <Radio size='small' checked={cell?.value as unknown as boolean ?? false} />
                        </Box>
                    )
                }
            },
            { id: 'flowId', label: 'Flow id', hidden: true },
            { id: 'contractId', label: 'Contract id', hidden: true },
            { id: 'contractName', label: 'Contract name' },
            { id: 'businessKey', label: 'Business key' },
            { id: 'productType', label: 'Product type', customRenderer: x => {
                if (!x.value) return null
                return <Chip size='small' color='secondary' label={x.value} />
            } },
            { id: 'nodesCount', label: '# of nodes' },
            { id: 'createdAt', label: 'Created at' }
        ]

        const clonedList = structuredClone(listUpdateFlowModal) ?? []
        const matchedContractId = clonedList?.find(x => x.flowId === selectedFlowUUID)
        if (matchedContractId)
            matchedContractId['action'] = true

        return (
            <Stack spacing={3} overflow='hidden' flex={1}>
                <Stack flexDirection='row' justifyContent='space-between'>
                    <Typography variant='h6'>Click an existing settlement to replace</Typography>
                    <IconButton
                        size='small'
                        onClick={() => {
                            setOpenBatchUpdateModal(false)
                            setPageUpdateFlowModal(1)
                            setSelectedFlowUUID(null)
                        }}>
                        <CloseRounded fontSize='small' />
                    </IconButton>
                </Stack>
                <SmartCard table={true}>
                    <SmartTable<CopyFromFlowItem>
                        hidePagination
                        columns={columns}
                        noCollapse
                        onClickRow={(index, cells, row) => {
                            setSelectedFlowUUID(row.cells[1].value as string)
                        }}
                        datasource={{
                            items: clonedList ?? []
                        }}
                        toolbar={{
                            columns: true,
                            search: ['contractName', 'nodesCount', 'createdAt', 'businessKey', 'productType'],
                            filters: true
                        }}
                        isLoading={getListCopyFromFlowStatus === 'loading'}
                    />
                </SmartCard>
                <Stack flexDirection='row' alignSelf='flex-end' gap={1}>
                    <Button
                        disabled={replaceFromFlowStatus === 'loading'}
                        onClick={() => {
                            setPageUpdateFlowModal(1)
                            setSelectedFlowUUID(null)
                        }}
                        size='small'
                        variant='outlined'
                        startIcon={<ArrowBackRounded fontSize='small' />}>go back
                    </Button>
                    <LoadingButton
                        loading={replaceFromFlowStatus === 'loading'}
                        disabled={!selectedFlowUUID}
                        loadingPosition='start'
                        onClick={() => replaceFromFlow(null)}
                        size='small'
                        variant='contained'
                        startIcon={<DoneRounded fontSize='small' />}>confirm selection
                    </LoadingButton>
                </Stack>
            </Stack>
        )
    }

    const renderConfirmClipboard = () => {
        return (
            <Stack spacing={3} sx={{ overflow: 'hidden', maxHeight: '75vh' }}>
                <Stack flexDirection='row' justifyContent='space-between'>
                    <Stack flexDirection='row' gap={1.5} alignItems='center'>
                        <ReportProblemRounded color='warning' />
                        <Stack>
                            <Typography variant='h6' fontWeight={500}>Are you sure you want to replace this flow?</Typography>
                            <Typography variant='subtitle2'>This will overwrite the current flow’s nodes and connections.</Typography>
                        </Stack>
                    </Stack>
                    <Box>
                        <IconButton
                            size='small'
                            onClick={() => {
                                setOpenBatchUpdateModal(false)
                                setPageUpdateFlowModal(1)
                            }}>
                            <CloseRounded fontSize='small' />
                        </IconButton>
                    </Box>
                </Stack>
                <Stack flexDirection='row' alignItems='center' gap={1.5}>
                    <Chip size='small' label={flow.nodes.length} color='info' />
                    <Typography variant='subtitle1'>Nodes are being replaced:</Typography>
                </Stack>
                <TableContainer>
                    <Table stickyHeader sx={{ overflowY: 'auto' }} size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <strong>Node name</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Node type</strong>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ '& > :last-child td, & > :last-child th': { border: 0 } }}>
                            {flow.nodes.map((node, index) => (
                                <TableRow key={index}>
                                    <TableCell>{node.name}</TableCell>
                                    <TableCell>{node.type}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Stack flexDirection='row' alignItems='center' justifyContent='flex-end' gap={1.5}>
                    <Button
                        disabled={replaceFromClipboardStatus === 'loading'}
                        variant='outlined'
                        size='small'
                        color='error'
                        onClick={() => setPageUpdateFlowModal(1)}>Cancel
                    </Button>
                    <LoadingButton
                        loading={replaceFromClipboardStatus === 'loading'}
                        onClick={() => replaceFromClipboard(null)}
                        loadingPosition='start'
                        variant='contained'
                        size='small'
                        startIcon={<DoneRounded fontSize='small' />}>Replace flow
                    </LoadingButton>
                </Stack>
            </Stack>
        )
    }

    return (
        <Paper sx={{ display: 'flex', flexDirection: 'column', width: pageUpdateFlowModal === 2 ? '55vw' : '36vw', maxHeight: pageUpdateFlowModal === 2 ? '80vh' : undefined, padding: 2, overflow: 'hidden' }}>
            {pageUpdateFlowModal === 1 && (
                renderReplaceFlowModal()
            )}
            {pageUpdateFlowModal === 2 && (
                renderSelectFlowModal()
            )}
            {pageUpdateFlowModal === 3 && (
                renderConfirmClipboard()
            )}
        </Paper>
    )
}

export default UpdateFlowModal
